/**
 * PostHog Analytics Helpers
 *
 * Helper functions for tracking events, page views, and user identification.
 * PostHog initialization happens in instrumentation-client.ts (runs before React).
 *
 * PostHog Cloud, reached through the FIRST-PARTY reverse proxy at
 * events.oekopunktemarkt.de (`apps/posthog-proxy`; the LandVest app has its own
 * at events.landvest.eu). Until the 2026-04-22 cutover this was a self-hosted
 * instance at analytics.oekopunktemarkt.de — that host is decommissioned, and
 * its DNS still resolves to a black hole, so anything still pointed at it hangs
 * rather than failing.
 *
 * The proxy is what keeps this first-party: cookies and ingestion stay on a
 * same-site subdomain, which is the basis of the no-consent-required position
 * below. Pointing NEXT_PUBLIC_POSTHOG_HOST straight at *.i.posthog.com would
 * make ingestion third-party and invalidate that reasoning — the host is a
 * compliance decision, not just a URL.
 *
 * Key features:
 * - Session recording enabled
 * - Person profiles for all users (including anonymous)
 * - Explicit event tracking (no autocapture)
 * - No consent required (first-party data collection)
 * - E2E test support via window.__E2E_ANALYTICS_ENABLED
 *
 * Environment variables:
 * - NEXT_PUBLIC_POSTHOG_KEY: PostHog project API key
 * - NEXT_PUBLIC_POSTHOG_HOST: ingestion host — the first-party proxy
 *   (https://events.oekopunktemarkt.de), not PostHog's own domain
 * - NEXT_PUBLIC_ENABLE_ANALYTICS: Feature flag to enable/disable analytics
 */

import posthog from "posthog-js";
import { withInternalUserFlag } from "@/lib/analytics/internal-users";
import {
  ATTRIBUTION_KEYS,
  type AttributionProperties,
  type PostHogContextPayload,
} from "@/lib/analytics/attribution";

// E2E Analytics Testing Types
export interface CapturedAnalyticsEvent {
  type: "event" | "pageview" | "identify" | "reset";
  event?: string;
  properties?: Record<string, unknown>;
  userId?: string;
  timestamp: number;
}

declare global {
  interface Window {
    __E2E_ANALYTICS_ENABLED?: boolean;
    __E2E_ANALYTICS_EVENTS?: CapturedAnalyticsEvent[];
  }
}

/**
 * Capture event for E2E testing (stores in window.__E2E_ANALYTICS_EVENTS)
 */
function captureForE2E(capturedEvent: CapturedAnalyticsEvent): void {
  if (typeof window !== "undefined" && window.__E2E_ANALYTICS_ENABLED) {
    window.__E2E_ANALYTICS_EVENTS = window.__E2E_ANALYTICS_EVENTS || [];
    window.__E2E_ANALYTICS_EVENTS.push(capturedEvent);
  }
}

// PostHog configuration
export const POSTHOG_CONFIG = {
  key: process.env.NEXT_PUBLIC_POSTHOG_KEY || "",
  host: process.env.NEXT_PUBLIC_POSTHOG_HOST || "",
};

/**
 * Check if PostHog is properly configured
 */
export function isPostHogConfigured(): boolean {
  return Boolean(POSTHOG_CONFIG.key && POSTHOG_CONFIG.host);
}

/**
 * Check if analytics feature is enabled
 */
export function isAnalyticsEnabled(): boolean {
  return process.env.NEXT_PUBLIC_ENABLE_ANALYTICS === "true";
}

/**
 * Get the current PostHog distinct_id from the client.
 *
 * Pass this to server-side code (e.g. API route handlers) so the server
 * can capture conversion events under the same person as prior client-side
 * funnel events — bypassing the anon→identified merge race that splits
 * funnels in PostHog when persistence is localStorage.
 */
export function getDistinctId(): string | null {
  const ph = getPostHog();
  if (ph) return ph.get_distinct_id() ?? null;
  // E2E fallback so server-side capture has something to attach to.
  if (typeof window !== "undefined" && window.__E2E_ANALYTICS_ENABLED) {
    return "e2e-anon-distinct-id";
  }
  return null;
}

/**
 * Read the browser's current attribution super properties (last-touch +
 * first-touch) from posthog-js. These were registered by
 * instrumentation-client.ts on page load and live in localStorage — the server
 * (posthog-node) can't see them, so we read them here and forward them to
 * server-side conversion captures (see getPostHogContext).
 */
export function getAttributionProperties(): AttributionProperties {
  const ph = getPostHog();
  if (!ph) return {};
  const out: AttributionProperties = {};
  for (const key of ATTRIBUTION_KEYS) {
    const value = ph.get_property(key);
    if (typeof value === "string" && value.length > 0) out[key] = value;
  }
  return out;
}

/**
 * Bundle the client-side PostHog context that server-side conversion captures
 * need but can't derive themselves: the session id (for the session-recording
 * link), the real current URL, and acquisition attribution. Pass the result
 * alongside `posthog_distinct_id` to the server (see build*Payload + the funnel
 * API routes). Empty pieces are omitted so callers can `if (Object.keys(...))`.
 */
export function getPostHogContext(): PostHogContextPayload {
  const ph = getPostHog();
  const ctx: PostHogContextPayload = {};
  if (!ph) return ctx;

  const sessionId = ph.get_session_id?.();
  if (typeof sessionId === "string" && sessionId.length > 0) {
    ctx.session_id = sessionId;
  }

  if (typeof window !== "undefined" && window.location?.href) {
    ctx.current_url = window.location.href;
  }

  const attribution = getAttributionProperties();
  if (Object.keys(attribution).length > 0) {
    ctx.attribution = attribution;
  }

  return ctx;
}

/**
 * Get the PostHog instance
 * Returns the posthog-js instance directly (initialized by instrumentation-client.ts)
 * Returns null if not in browser or not initialized
 */
export function getPostHog(): typeof posthog | null {
  if (typeof window === "undefined") return null;
  // posthog-js uses __loaded to indicate initialization
  if (!(posthog as unknown as { __loaded?: boolean }).__loaded) return null;
  return posthog;
}

/**
 * Get the PostHog instance for capturing events.
 * Unlike getPostHog(), this checks posthog.config (set synchronously during init())
 * instead of __loaded (set asynchronously in the loaded callback).
 * This allows events fired on mount (e.g. useEffect) to be queued by posthog-js
 * before the loaded callback completes.
 */
function getPostHogForCapture(): typeof posthog | null {
  if (typeof window === "undefined") return null;
  // posthog.config is set synchronously by posthog.init(), so it's available
  // immediately. posthog-js queues events internally until the library is fully loaded.
  if (!posthog.config) return null;
  return posthog;
}

/**
 * Enable analytics tracking (call when user consents)
 */
export function enableTracking(): void {
  const ph = getPostHog();
  if (ph) {
    ph.opt_in_capturing();
    console.debug("[PostHog] Tracking enabled");
  }
}

/**
 * Disable analytics tracking (call when user revokes consent)
 */
export function disableTracking(): void {
  const ph = getPostHog();
  if (ph) {
    ph.opt_out_capturing();
    console.debug("[PostHog] Tracking disabled");
  }
}

/**
 * Track a page view
 * No consent check needed - first-party analytics
 */
export function trackPageView(url?: string): void {
  const currentUrl = typeof window !== "undefined" ? url || window.location.href : url;
  const properties = { $current_url: currentUrl };

  // E2E capture (before PostHog)
  captureForE2E({
    type: "pageview",
    event: "$pageview",
    properties,
    timestamp: Date.now(),
  });

  const ph = getPostHogForCapture();
  if (ph) {
    ph.capture("$pageview", properties);
  }
}

/**
 * Track a custom event
 * No consent check needed - first-party analytics
 *
 * @param options.sendInstantly - Bypass the batch queue (use for events
 *   fired right before page navigation, e.g. funnel completion)
 */
export function trackEvent(
  event: string,
  properties?: Record<string, unknown>,
  options?: { sendInstantly?: boolean }
): void {
  // E2E capture (before PostHog)
  captureForE2E({
    type: "event",
    event,
    properties,
    timestamp: Date.now(),
  });

  const ph = getPostHogForCapture();
  if (ph) {
    ph.capture(event, properties, options?.sendInstantly ? { send_instantly: true } : undefined);
  }
}

/**
 * Track a custom event, bypassing the batched queue.
 * Convenience wrapper — use for critical events (e.g. funnel completion)
 * that must not be lost if the page navigates immediately after.
 */
export function trackEventInstantly(event: string, properties?: Record<string, unknown>): void {
  trackEvent(event, properties, { sendInstantly: true });
}

/**
 * Identify a user (creates a person profile in PostHog)
 * No consent check needed - first-party analytics
 */
export function identifyUser(userId: string, properties?: Record<string, unknown>): void {
  // Auto-tag internal/staff users so dashboards filter them out immediately,
  // without waiting for the "Internal / Test users" cohort to recalculate.
  const enriched = withInternalUserFlag(properties);

  // E2E capture (before PostHog)
  captureForE2E({
    type: "identify",
    userId,
    properties: enriched,
    timestamp: Date.now(),
  });

  const ph = getPostHogForCapture();
  if (ph) {
    ph.identify(userId, enriched);
  }
}

/**
 * Reset the user identity (call on logout)
 */
export function resetUser(): void {
  // E2E capture (before PostHog)
  captureForE2E({
    type: "reset",
    timestamp: Date.now(),
  });

  const ph = getPostHogForCapture();
  if (ph) {
    ph.reset();
  }
}

/**
 * Capture an exception manually
 * Use this in error boundaries to report errors to PostHog
 *
 * @param error - The error to capture
 * @param additionalProperties - Additional properties to include with the exception
 */
export function captureException(
  error: Error,
  additionalProperties?: Record<string, unknown>
): void {
  const ph = getPostHogForCapture();
  if (ph) {
    ph.captureException(error, additionalProperties);
  }
}
